import { ensureReady, semanticTopK } from './semanticSearch.js';
import { plainAskFollowUp, stripAskActions } from './askActions.js';

const STOP = new Set([
  'the', 'a', 'an', 'of', 'on', 'in', 'to', 'is', 'are', 'was', 'were', 'be',
  'and', 'or', 'for', 'with', 'at', 'by', 'from', 'as', 'it', 'i', 'my', 'me',
  'this', 'that', 'how', 'what', 'we', 'you', 'they', 'but', 'so', 'if', 'into',
  'about', 'out', 'up', 'over', 'than', 'then', 'has', 'have', 'had', 'do',
]);

let itemsPromise = null;

export function searchTokens(query) {
  const all = String(query).toLowerCase().split(/\s+/)
    .map(t => t.replace(/[^\w]+/g, ''))
    .filter(t => t.length >= 2);
  const meaningful = all.filter(t => !STOP.has(t));
  return meaningful.length ? meaningful : all;
}

export async function loadSearchItems() {
  if (!itemsPromise) {
    itemsPromise = fetch('/assets/search-index.json', { cache: 'no-cache' })
      .then((res) => {
        if (!res.ok) throw new Error(`search-index.json ${res.status}`);
        return res.json();
      });
  }
  return itemsPromise;
}

// TF-IDF-flavoured lexical ranking over the search chunks. Exported so the
// search-story page can show this stage of the pipeline on its own.
export function lexicalRank(query, items) {
  const toks = [...new Set(searchTokens(query))].filter(t => t.length >= 3);
  const N = items.length;
  const hays = items.map(it => ({ id: it.id, hay: `${it.title} ${it.body}`.toLowerCase() }));
  const df = {};
  for (const t of toks) df[t] = hays.filter(h => h.hay.includes(t)).length;
  return hays.map(h => {
    let s = 0;
    for (const t of toks) if (df[t] > 0 && h.hay.includes(t)) s += Math.log(1 + N / df[t]);
    return { id: h.id, s };
  }).filter(x => x.s > 0).sort((a, b) => b.s - a.s);
}

// Weighted reciprocal-rank fusion of the two rankings, with the top lexical
// hits pinned first. Returns [{ id, score, rank }], best first.
export function rrfRanked(lex, sem, { K = 60, lexWeight = 2, semWeight = 1, forceLex = 3 } = {}) {
  const score = new Map();
  const add = (arr, w) => arr.forEach((x, i) => score.set(x.id, (score.get(x.id) || 0) + w / (K + i)));
  add(lex, lexWeight);
  add(sem, semWeight);
  const ordered = [...score.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
  const forced = lex.slice(0, forceLex).map(x => x.id);
  return [...new Set([...forced, ...ordered])]
    .map((id, i) => ({ id, score: score.get(id) || 0, rank: i + 1 }));
}

// Ids only, best first. Unchanged contract for callers that just want the order.
export function rrfFuse(lex, sem, opts = {}) {
  return rrfRanked(lex, sem, opts).map(x => x.id);
}

// The ranking internals of the last Ask retrieval. Only the browser ever sees
// these (lexical/semantic ranks and scores, and how fusion reordered them), so
// they are stashed here for the telemetry beacon to pick up after the answer.
const MAX_TRACE_ROWS = 60;
let lastTrace = null;

function buildTrace({ query, lex, sem, fused, itemsById, n, ms }) {
  const lexRank = new Map(lex.map((x, i) => [x.id, { rank: i + 1, score: x.s }]));
  const semRank = new Map(sem.map((x, i) => [x.id, { rank: i + 1, score: x.score }]));
  const topIds = new Set(fused.slice(0, n).map(f => f.id));

  const rows = fused.slice(0, MAX_TRACE_ROWS).map((f) => {
    const item = itemsById[f.id] || {};
    const l = lexRank.get(f.id) || null;
    const s = semRank.get(f.id) || null;
    return {
      chunk_id: String(f.id),
      route: item.route || null,
      anchor: item.anchor || null,
      page: item.page || null,
      title: String(item.title || '').slice(0, 200) || null,
      lex_rank: l ? l.rank : null,
      lex_score: l ? Number(l.score.toFixed(5)) : null,
      sem_rank: s ? s.rank : null,
      sem_score: s ? Number(s.score.toFixed(5)) : null,
      rrf_score: Number(f.score.toFixed(6)),
      final_rank: f.rank,
      in_topk: topIds.has(f.id),
    };
  });

  return {
    query,
    retrieval_ms: ms,
    n_lex: lex.length,
    n_sem: sem.length,
    sem_top_score: sem.length ? Number(sem[0].score.toFixed(5)) : null,
    rows,
  };
}

// Read and clear the stash, so a stale trace can never be attached to a later
// question that failed before retrieving anything.
export function takeRetrievalTrace() {
  const trace = lastTrace;
  lastTrace = null;
  return trace;
}

export async function retrieveAskContexts(query, n = 8, existingItems) {
  const items = existingItems && existingItems.length ? existingItems : await loadSearchItems();
  const itemsById = {};
  for (const it of items) itemsById[it.id] = it;

  const started = Date.now();
  await ensureReady();
  const sem = await semanticTopK(query, 12);
  const lex = lexicalRank(query, items);
  const fused = rrfRanked(lex, sem.map(h => ({ id: h.id })));
  const ids = fused.slice(0, n).map(f => f.id);

  try {
    lastTrace = buildTrace({ query, lex, sem, fused, itemsById, n, ms: Date.now() - started });
  } catch {
    lastTrace = null;
  }

  return ids.map(id => itemsById[id]).filter(Boolean);
}

function isFragmentItem(item) {
  return item && item.route === '/fragments';
}

function seededUnit(seed, value) {
  const input = `${seed}:${value}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  hash += hash << 13;
  hash ^= hash >>> 7;
  hash += hash << 3;
  hash ^= hash >>> 17;
  hash += hash << 5;
  return (hash >>> 0) / 4294967296;
}

// Keep semantic order as a weak preference, then vary the Fragment pool per
// request. The model decides how those sources serve the visitor's intent.
// This avoids a keyword gate while keeping repeated open-ended requests from
// falling into a fixed A/B loop caused by context-position bias.
export function diversifyAskWritingContexts(contexts, query, seed, recentSourceIds = []) {
  if (!seed) return contexts;
  const base = contexts.filter((item) => !isFragmentItem(item));
  const fragments = contexts.filter(isFragmentItem);
  const recent = new Set((recentSourceIds || []).map((id) => String(id)));
  const total = Math.max(1, fragments.length - 1);
  const varied = fragments
    .map((item, index) => ({
      item,
      score: seededUnit(seed, item.source_id || item.id || index) * 0.72
        + ((total - index) / total) * 0.28
        - (recent.has(String(item.source_id || item.id || '')) ? 2 : 0),
    }))
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
  return [...base, ...varied];
}

export function sourceDisplayTitle(source) {
  const title = String(source?.title || source?.page || 'Untitled')
    .replace(/\s+\(\d+\)$/, '')
    .replace(/\s+\/\s+[\u3400-\u9fff].*$/, '')
    .trim();
  return title || 'Untitled';
}

export function sourceKey(source) {
  return `${source?.route || ''}#${source?.anchor || sourceDisplayTitle(source)}`;
}

export function normalizeAskSource(source) {
  const normalized = {
    ...source,
    title: sourceDisplayTitle(source),
  };
  return {
    ...normalized,
    // `id` becomes the route#anchor key used for de-duplication in the UI; keep
    // the original chunk id so telemetry can join back to the retrieval rows.
    chunk_id: source?.id === undefined || source?.id === null ? null : String(source.id),
    id: sourceKey(normalized),
  };
}

function currentAskInputText(preferPalette = false) {
  if (typeof document === 'undefined') return '';
  const selector = preferPalette
    ? '.sp-input, #homeAsk .home-ask-input'
    : '#homeAsk .home-ask-input, .sp-input';
  const input = document.querySelector(selector);
  return input && 'value' in input ? String(input.value || '') : '';
}

function normalizeAllAskArgs(query, existingItems) {
  const firstArgIsItems = Array.isArray(query);
  const items = firstArgIsItems ? query : existingItems;
  let actualQuery = typeof query === 'string' ? query : '';
  if (!actualQuery) actualQuery = currentAskInputText(firstArgIsItems);
  return { actualQuery, items, firstArgIsItems };
}

export async function allAskContexts(query = '', existingItems) {
  const { actualQuery, items: existing, firstArgIsItems } = normalizeAllAskArgs(query, existingItems);
  const items = existing && existing.length ? existing : await loadSearchItems();
  const base = items.filter(it => !isFragmentItem(it)).sort((a, b) => (a.id || 0) - (b.id || 0));
  const fragments = items.filter(isFragmentItem).sort((a, b) => (a.id || 0) - (b.id || 0));

  if (!actualQuery) return firstArgIsItems ? [...base, ...fragments] : base;

  const retrieved = await retrieveAskContexts(actualQuery, 12, items);
  const fragmentHits = retrieved.filter(isFragmentItem);
  const hitIds = new Set(fragmentHits.map((item) => item.id));
  // Every Fragment is approved public material and the complete Ask payload is
  // kept within the Worker's bounded whole-site context. Put semantic hits first, then
  // include the remaining writing so the model can answer cross-language and
  // open-ended requests from Henry's actual words instead of claiming that no
  // suitable material exists.
  const remainingFragments = fragments.filter((item) => !hitIds.has(item.id));
  return [...base, ...fragmentHits, ...remainingFragments];
}

export function cleanAskContextText(body) {
  return String(body || '')
    .replace(/^(Research papers[^.]*\.\s*|Hobby, hobbies[^.]*\.\s*)+/i, '')
    .replace(/\s*Years:\s*[\d\s]+\.?\s*$/i, '')
    .trim();
}

// The model appends a short "[[FOLLOWUPS]]" block to the end of its answer so
// the visitor sees clean prose while the site turns the block into the
// "Keep exploring" chips. Parsed leniently: if the marker or the question
// lines are missing, the caller falls back to hand-picked questions.
export const ASK_FOLLOWUP_MARKER = '[[FOLLOWUPS]]';
export const ASK_VIEW_MARKER = '[[VIEW]]';
export const DEFAULT_VIEW_OFFER = Object.freeze({
  title: 'See this question in context',
  description: 'Use AI to connect the most relevant content from across Henry’s site into a focused view.',
});

export function askResponseIsComplete(answer) {
  const value = String(answer || '');
  const viewAt = value.lastIndexOf(ASK_VIEW_MARKER);
  const followUpsAt = value.lastIndexOf(ASK_FOLLOWUP_MARKER);
  if (viewAt < 0 || followUpsAt <= viewAt) return false;
  const visibleAnswer = value.slice(0, viewAt).trim();
  const followUps = value.slice(followUpsAt + ASK_FOLLOWUP_MARKER.length)
    .split('\n')
    .map((line) => line.replace(/^[-–—*•]\s*/, '').trim())
    .filter((line) => line && /[?？]$/.test(line));
  return visibleAnswer.length >= 12 && followUps.length === 3;
}

export function looksLikeViewBuildRequest(query) {
  const text = String(query || '').trim().toLowerCase();
  if (!text) return false;
  const buildVerb = /\b(build|make|create|assemble|compose|organise|organize|put together)\b/.test(text);
  const viewNoun = /\b(page|view|site|site view|composed view|compose)\b/.test(text) || /as a page\b/.test(text);
  return buildVerb && (viewNoun || text.includes('around') || text.includes('show me'));
}

function cleanViewOfferField(value, maxLength) {
  const text = String(value || '')
    .replace(/\[\[(?:VIEW|FOLLOWUPS)\]\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return text && text.length <= maxLength ? text : '';
}

export function askViewOffer(answer) {
  const text = String(answer || '');
  // The [[VIEW]] marker is the model's explicit routing decision. Site-feature
  // answers are instructed to return "none" there, so a genuine "offer" is
  // trusted even when the model also appended stray entry-point actions.
  const idx = text.lastIndexOf(ASK_VIEW_MARKER);
  if (idx < 0) return { routing: 'none', ...DEFAULT_VIEW_OFFER };
  const block = text.slice(idx + ASK_VIEW_MARKER.length)
    .split(ASK_FOLLOWUP_MARKER)[0]
    .trim();
  const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
  const routing = ['build', 'offer'].includes(String(lines[0] || '').toLowerCase())
    ? String(lines[0]).toLowerCase()
    : 'none';
  const titleLine = lines.find((line) => /^title\s*:/i.test(line));
  const descriptionLine = lines.find((line) => /^description\s*:/i.test(line));
  const title = cleanViewOfferField(titleLine?.replace(/^title\s*:\s*/i, ''), 90);
  const description = cleanViewOfferField(descriptionLine?.replace(/^description\s*:\s*/i, ''), 220);
  return {
    routing,
    title: title || DEFAULT_VIEW_OFFER.title,
    description: description || DEFAULT_VIEW_OFFER.description,
  };
}

export function askViewRouting(answer) {
  return askViewOffer(answer).routing;
}

export function stripAskViewBlock(answer) {
  const text = String(answer || '');
  const idx = text.lastIndexOf(ASK_VIEW_MARKER);
  if (idx < 0) return text;
  return text.slice(0, idx).trim();
}

export function splitAnswerFollowUps(answer) {
  const raw = String(answer || '');
  const idx = raw.lastIndexOf(ASK_FOLLOWUP_MARKER);
  const stripBlocks = (text) => stripAskActions(stripAskViewBlock(text));
  if (idx < 0) return { answer: stripBlocks(raw), followUps: [] };
  const body = stripBlocks(raw.slice(0, idx));
  const block = raw.slice(idx + ASK_FOLLOWUP_MARKER.length);
  const followUps = block
    .split('\n')
    .map((line) => line.replace(/^[-–—*•]\s*/, '').trim())
    .map(plainAskFollowUp)
    .filter((line) => line && /[?？]$/.test(line))
    .slice(0, 3);
  return { answer: body, followUps };
}

// The text to show as the answer: marker block removed, citation phrases
// cleaned. Safe to call on a still-streaming answer.
export function cleanAskAnswer(answer) {
  return stripAnswerCitations(splitAnswerFollowUps(answer).answer);
}

// AI-generated "Keep exploring" questions, empty when the model did not
// provide them (or is still streaming).
export function askAnswerFollowUps(answer) {
  return splitAnswerFollowUps(answer).followUps;
}

// Minimum cosine similarity for a follow-up question to be offered as a chip.
// The on-device embedder already ranks every passage for retrieval; a follow-up
// that cannot retrieve a relevant passage would just produce another "I don't
// have that on the site" answer, so it is dropped. Calibrated with the search
// floor in mind: clearly off-topic <= ~0.22, genuine matches mostly >= ~0.45.
export const FOLLOWUP_SCORE_FLOOR = 0.30;

// Keep only follow-up questions that actually retrieve a relevant passage on
// the site. Falls back to trusting the model's list if the embedder errors.
export async function groundAskFollowUps(questions, floor = FOLLOWUP_SCORE_FLOOR) {
  const list = (questions || []).map((q) => String(q).trim()).filter(Boolean).slice(0, 4);
  if (!list.length) return [];
  try {
    const scored = await Promise.all(list.map(async (q) => {
      const top = await semanticTopK(q, 1);
      return { q, score: top[0] ? top[0].score : 0 };
    }));
    return scored
      .filter((r) => r.score >= floor)
      .map((r) => r.q)
      .slice(0, 3);
  } catch {
    return list.slice(0, 3);
  }
}

export function stripAnswerCitations(answer) {
  return String(answer || '')
    .replace(/\s*,?\s*\bas\s+(?:mentioned|shown|described|noted|stated|indicated|covered|referenced)\s+(?:in|by)\s*(?:\[[\d,\s]+\]\s*(?:(?:,|\band\b|\bor\b)\s*)?)+(?!\w)/gi, '')
    .replace(/\b(?:as\s+)?(?:mentioned|shown|described|noted|stated|indicated|covered|referenced)\s+(?:in|by)\s*(?:\[[\d,\s]+\]\s*(?:(?:,|\band\b|\bor\b)\s*)?)+,?\s*/gi, '')
    .replace(/\s*(?:\[[\d,\s]+\]\s*(?:(?:,|\band\b|\bor\b)\s*)?){2,}/gi, '')
    .replace(/\s*\[[\d,\s]+\]/g, '')
    .replace(/\s*\[[\d,\s]*$/g, '')
    .replace(/\b(?:as\s+)?(?:mentioned|shown|described|noted|stated|indicated|covered|referenced)\s+(?:in|by)\s+(?:and|or)(?=[\s,.!?]|$)/gi, '')
    .replace(/(?:,\s*){2,}/g, ', ')
    .replace(/,+\s*([.!?])/g, '$1')
    .replace(/([.!?]),(\s|$)/g, '$1$2')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/,\s*$/g, '')
    .trim();
}

export function citedSourcesFromAnswer(answer, sources) {
  const numbered = sources.map((s, i) => ({ ...s, n: i + 1 }));
  const cited = new Set();
  const re = /\[([\d,\s]+)\]/g;
  let m;
  while ((m = re.exec(answer))) {
    m[1].split(/[\s,]+/).filter(Boolean).forEach((n) => cited.add(parseInt(n, 10)));
  }
  if (!cited.size) return [];

  const out = [];
  const seen = new Set();
  for (const source of numbered) {
    if (!cited.has(source.n)) continue;
    const normalized = normalizeAskSource(source);
    const key = sourceKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  return out;
}
